import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Heart } from "lucide-react";
import { NAV, FOUNDATION } from "@/data/content";
import { useLocale } from "@/context/LocaleContext";
import { useSettings } from "@/context/SettingsContext";
import LocaleSwitcher from "@/components/common/LocaleSwitcher";
import { getImageUrl } from "@/lib/api";

// Map NAV labels (ID) to i18n keys
const NAV_KEY = {
  "Beranda": "nav.home",
  "Tentang": "nav.about",
  "Program": "nav.programs",
  "Proyek": "nav.projects",
  "Impact": "nav.impact",
  "Berita": "nav.news",
  "Galeri": "nav.gallery",
  "Mitra": "nav.partners",
  "Publikasi": "nav.publications",
  "Tim": "nav.team",
  "Volunteer": "nav.volunteer",
  "Donasi": "nav.donate",
  "Kontak": "nav.contact",
  "Tentang Yayasan": "nav.about.foundation",
  "Sejarah": "nav.about.history",
  "Visi & Misi": "nav.about.vision",
  "Filosofi": "nav.about.philosophy",
  "Nilai-nilai": "nav.about.values",
  "Semua Program": "nav.programs.all",
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const location = useLocation();
  const { t } = useLocale();
  const { settings, customNavPages } = useSettings();

  const brand = settings?.navbar || {};
  const donateLabel = brand.donate_button_label || t("nav.donate");
  const donateUrl = brand.donate_button_url || "/donasi";
  const brandText = brand.brand_text || "SAUYUNAN";
  const brandSubtitle = brand.brand_subtitle || "Foundation";
  const logoSrc = settings?.foundation?.logo_url || FOUNDATION.logo;

  // Merge custom nav pages into "Tentang" dropdown if nav_parent matches, else append to top-level
  const mergedNav = [...NAV];
  (customNavPages || []).forEach((p) => {
    const item = { label: p.nav_label || p.title, to: `/p/${p.slug}` };
    if (p.nav_parent) {
      const parent = mergedNav.find((n) => n.label.toLowerCase() === p.nav_parent.toLowerCase());
      if (parent) { parent.children = [...(parent.children || []), item]; return; }
    }
    mergedNav.push(item);
  });

  const label = (l) => (NAV_KEY[l] ? t(NAV_KEY[l]) : l);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setOpenSub(null); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,.04)]" : "bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="container-sf flex h-20 items-center justify-between gap-6">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
          <img src={getImageUrl(logoSrc)} alt={brandText} className="h-11 w-11 object-contain" />
          <div className="hidden sm:block leading-tight">
            <div className="text-[15px] font-bold tracking-tight text-[var(--brand-blue)]">{brandText}</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--brand-orange)]">{brandSubtitle}</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1" data-testid="navbar-desktop">
          {mergedNav.filter((i) => !["Volunteer", "Donasi"].includes(i.label)).map((item) => (
            <NavItem key={item.label} item={item} label={label} />
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          <LocaleSwitcher />
          <Link to={donateUrl} data-testid="navbar-donate-btn" className="btn-primary !py-2.5 !px-5 !text-sm">
            <Heart className="h-4 w-4" /> {donateLabel}
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white max-h-[85vh] overflow-y-auto" data-testid="navbar-mobile-panel">
          <div className="container-sf py-6 flex flex-col gap-1">
            <div className="pb-3 mb-2 border-b border-slate-100"><LocaleSwitcher /></div>
            {mergedNav.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => setOpenSub(openSub === item.label ? null : item.label)}
                      className="w-full flex items-center justify-between py-3 px-4 rounded-xl hover:bg-slate-50 text-left font-semibold text-slate-800"
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    >
                      <span>{label(item.label)}</span>
                      <ChevronDown className={`h-4 w-4 transition ${openSub === item.label ? "rotate-180" : ""}`} />
                    </button>
                    {openSub === item.label && (
                      <div className="ml-4 pl-4 border-l-2 border-slate-200 my-1 flex flex-col">
                        {item.children.map((c) => (
                          <Link
                            key={c.label}
                            to={c.to}
                            className="py-2.5 px-3 text-sm text-slate-600 hover:text-[var(--brand-blue)]"
                          >
                            {label(c.label)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.to}
                    className="block py-3 px-4 rounded-xl hover:bg-slate-50 font-semibold text-slate-800"
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    {label(item.label)}
                  </Link>
                )}
              </div>
            ))}
            <Link to={donateUrl} className="btn-primary mt-4 justify-center" data-testid="mobile-nav-donate">
              <Heart className="h-4 w-4" /> {donateLabel}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ item, label }) {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `px-3.5 py-2 rounded-full text-sm font-semibold transition ${
            isActive ? "text-[var(--brand-blue)]" : "text-slate-700 hover:text-[var(--brand-blue)]"
          }`
        }
      >
        {label(item.label)}
      </NavLink>
    );
  }
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className="px-3.5 py-2 rounded-full text-sm font-semibold text-slate-700 hover:text-[var(--brand-blue)] transition flex items-center gap-1"
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        {label(item.label)} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-3 min-w-[260px]" data-testid={`dropdown-${item.label.toLowerCase()}`}>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden py-2">
            {item.children.map((c) => (
              <Link
                key={c.label}
                to={c.to}
                className="block px-5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[var(--brand-blue)] transition"
              >
                {label(c.label)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
